import React, { useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Autocomplete, Box, Checkbox, Chip, Paper, TextField, Tooltip, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { i18n } from "i18next";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Tooltip as RechartsTooltip } from 'recharts';

import { getUsageCategoryList, Usage, UsageCategoryKey, UsageInterval, UsageMetric } from "routes/apiUsage/types/Usage";
import { CategoricalChartState } from "recharts/types/chart/types";
import UsageIntervalTooltip from "./UsageIntervalTooltip";
import { toAbbreviatedNumber } from "components/formatting/AbbreviateNumber";

const UsageCategoryList = getUsageCategoryList();

interface UsageBarChartProps {
  metric: UsageMetric;
  onChange: (selectedUsageCategories: Array<UsageCategoryKey>) => void;
  selectedUsageCategories: Array<UsageCategoryKey>;
  usage: Usage;
  usageCategoryOptions: Array<UsageCategoryKey>;
}

export function getNonEmptyUsageCategories(usage: Usage, metric: UsageMetric): Array<UsageCategoryKey> {
  const result = UsageCategoryList.filter((category) => {
    const totalUsage = usage.total[metric][category];
    return !!totalUsage;
  });
  return result;
}

export function sortUsageCategories(categories: Array<UsageCategoryKey>, usage: Usage, metric: UsageMetric, i18n: i18n): Array<UsageCategoryKey> {
  const result = categories.sort((a, b) => {
    // Categories with usage first, categories with no usage last
    const aUsage = usage.total[metric][a];
    const bUsage = usage.total[metric][b];
    if (aUsage && !bUsage) return -1;
    if (!aUsage && bUsage) return 1;
    // Alphabetical by title
    const aTitle = i18n.t(`usage.categories.${a}`).toLowerCase();
    const bTitle = i18n.t(`usage.categories.${b}`).toLowerCase();
    if (aTitle > bTitle) return 1;
    if (aTitle < bTitle) return -1;
    return 0;
  });
  return result;
}

export default function UsageBarChart(props: UsageBarChartProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const [hovered, setHovered] = useState<UsageInterval | undefined>();

  const bars = props.selectedUsageCategories.map((usageCategoryKey, index) => {
    const name = i18n.t(`usage.categories.${usageCategoryKey}`);
    const colorIndex = UsageCategoryList.indexOf(usageCategoryKey);
    return (
      <Bar
        key={usageCategoryKey}
        dataKey={`${usageCategoryKey}_${props.metric}`}
        fill={theme.colors.namedColors.chartColorArray[colorIndex]}
        name={name}
        stackId={props.metric}
      />
    )
  });

  return (
    <Paper sx={{p: 1}}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end"
        }}>
        <Autocomplete
          disableClearable
          disableCloseOnSelect
          multiple
          options={props.usageCategoryOptions}
          onChange={(event, value) => props.onChange(value) }
          renderInput={(params) => <TextField autoComplete="off" label="Usage Categories" {...params} />}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps} style={{whiteSpace: "nowrap"}}>
                <Checkbox
                  checked={selected}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  size="small"
                  style={{ marginRight: 8 }}
                />
                {i18n.t(`usage.categories.${option}`)}
              </li>
            );
          }}
          renderValue={(value: readonly string[], getItemProps) =>
            value.map((option: string, index: number) => {
              const { key, ...tagProps } = getItemProps({ index });
              const colorIndex = UsageCategoryList.indexOf(option as UsageCategoryKey);
              return (
                <Chip
                  key={option}
                  label={i18n.t(`usage.categories.${option}`)}
                  sx={{backgroundColor: theme.colors.namedColors.chartColorArray[colorIndex]}}
                  {...tagProps}
                />
              );
            })
          }
          size="small"
          sx={{minWidth: 200, width: "fit-content"}}
          value={props.selectedUsageCategories}
        />
      </Box>
      <Tooltip
        followCursor
        open={!!hovered}
        title={hovered &&
          <UsageIntervalTooltip
            activeUsageCategories={props.selectedUsageCategories}
            activeUsageMetric={props.metric}
            usageInterval={hovered}
          />}
      >
        <Box>
        <ResponsiveContainer height={240} width="100%">
          <BarChart
            data={props.usage.timeline}
            onMouseLeave={() => setHovered(undefined)}
            onMouseMove={(nextState: CategoricalChartState, ...args) => {
              let usageInterval: UsageInterval | undefined = undefined;
              if (typeof nextState.activeTooltipIndex === "number" && nextState.activeTooltipIndex >= 0) {
                usageInterval = props.usage.timeline[nextState.activeTooltipIndex];
              }
              setHovered(usageInterval);
            }}
            style={{ cursor: "pointer" }}
          >
            {/* We need this Recharts tooltip to show the hover background effects */}
            <RechartsTooltip content={() => null} />
            <CartesianGrid strokeOpacity={0.65} strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis tickFormatter={(value) => {
              let abbreviated = toAbbreviatedNumber(i18n, value);
              return abbreviated.toString();
            }} />
            {bars}
          </BarChart>
        </ResponsiveContainer>
        </Box>
      </Tooltip>
    </Paper>
  );
}